import { monthDiff } from 'cpool-utils';
import { z } from 'zod';

import {
    schemaConsorsNationalityISOAustria,
    schemaConsorsNationalityISOEU,
    schemaConsorsNationalityISOGermany,
    schemaConsorsNationalityISOOthers,
} from './nationality.schema';
import { schemaConsorsSubscriberMain } from './subscriberMain.schema';
import { schemaConsorsSubscriberPartner } from './subscriberPartner.schema';

export const schemaConsorsSubscriber = z
    .discriminatedUnion('rolePlaying', [schemaConsorsSubscriberMain, schemaConsorsSubscriberPartner])
    .refine(
        (data) => {
            if (data.numberOfChildren > 0 && data.income.isChildBenefitInSalery === undefined) {
                return false;
            }
            return true;
        },
        {
            message: 'income.isChildBenefitInSalery may not be undefined when numberOfChildren is greater than 0',
        },
    )
    .refine(
        (data) => {
            if (
                data.employmentDetails.profession === 'PENSIONER' &&
                data.income.retirementPensionSupplement === undefined
            ) {
                return false;
            }
            return true;
        },
        {
            message: 'income.retirementPensionSupplement may not be undefined when profession is "PENSIONER"',
        },
    )
    .refine(
        (data) => {
            if (
                (data.housingSituation === 'EMPLOYER' ||
                    data.housingSituation === 'RENTER' ||
                    data.housingSituation === 'OWNER_WITH_MORTGAGE') &&
                data.expense.warmRent === undefined
            ) {
                return false;
            }
            return true;
        },
        {
            message:
                'expense.warmRent may not be undefined when housingSituation is EMPLOYER, RENTER or OWNER_WITH_MORTGAGE',
        },
    )
    .refine(
        (data) => {
            if (data.rolePlaying === 'PARTNER') return true;
            if (data.contactAddress.validFrom === undefined) return true;
            const validFromMonthsDiff = monthDiff(new Date(data.contactAddress.validFrom), new Date());
            if (
                typeof validFromMonthsDiff === 'number' &&
                validFromMonthsDiff < 25 &&
                data.previousAddress.street === undefined
            )
                return false;
            return true;
        },
        {
            message:
                'previousAddress-street may not be undefined when contactAddress.validFrom is less than 25 months ago',
        },
    )
    .refine(
        (data) => {
            if (data.rolePlaying === 'PARTNER') return true;
            if (data.contactAddress.validFrom === undefined) return true;
            const validFromMonthsDiff = monthDiff(new Date(data.contactAddress.validFrom), new Date());
            if (
                typeof validFromMonthsDiff === 'number' &&
                validFromMonthsDiff < 25 &&
                data.previousAddress.city === undefined
            )
                return false;
            return true;
        },
        {
            message:
                'previousAddress-city may not be undefined when contactAddress.validFrom is less than 25 months ago',
        },
    )
    .refine(
        (data) => {
            if (data.rolePlaying === 'PARTNER') return true;
            if (data.contactAddress.validFrom === undefined) return true;
            const validFromMonthsDiff = monthDiff(new Date(data.contactAddress.validFrom), new Date());
            if (
                typeof validFromMonthsDiff === 'number' &&
                validFromMonthsDiff < 25 &&
                data.previousAddress.zipcode === undefined
            )
                return false;
            return true;
        },
        {
            message:
                'previousAddress-zipcode may not be undefined when contactAddress.validFrom is less than 25 months ago',
        },
    )
    .refine(
        (data) => {
            if (
                schemaConsorsNationalityISOGermany.safeParse(data.nationality).success &&
                (data.identity.identificationType === 'EU_PASS' ||
                    data.identity.identificationType === 'EU_ID_CARD' ||
                    data.identity.identificationType === 'FOREIGN_PASS_PERMISION')
            )
                return false;
            return true;
        },
        {
            message:
                'when nationality is Germany, identificationType must be either GERMAN_PASS, GERMAN_ID_CARD, GERMAN_ID_CARD_TMP or NO_PASS',
        },
    )
    .refine(
        (data) => {
            if (
                schemaConsorsNationalityISOOthers.safeParse(data.nationality).success === true &&
                !(
                    data.identity.identificationType === 'FOREIGN_PASS_PERMISION' ||
                    data.identity.identificationType === 'NO_PASS'
                )
            )
                return false;
            return true;
        },
        {
            message: 'when nationality is non EU, identificationType must be either FOREIGN_PASS_PERMISION or NO_PASS',
        },
    )
    .refine(
        (data) => {
            if (
                schemaConsorsNationalityISOOthers.safeParse(data.nationality).success === true &&
                data.identity.residencePermitType === undefined
            )
                return false;
            return true;
        },
        {
            message: 'when nationality is non EU, residencePermitType cannot be undefined',
        },
    )
    .refine(
        (data) => {
            if (
                (schemaConsorsNationalityISOEU.safeParse(data.nationality).success === true ||
                    schemaConsorsNationalityISOAustria.safeParse(data.nationality).success === true) &&
                (data.identity.identificationType === 'GERMAN_PASS' ||
                    data.identity.identificationType === 'GERMAN_ID_CARD' ||
                    data.identity.identificationType === 'GERMAN_ID_CARD_TMP' ||
                    data.identity.identificationType === 'FOREIGN_PASS_PERMISION')
            )
                return false;
            return true;
        },
        {
            message: 'when nationality is EU, identificationType must be either EU_PASS, EU_ID_CARD, or NO_PASS',
        },
    )
    .refine(
        (data) => {
            if (!data.employmentDetails.professionBeginDate) return true;
            if (monthDiff(new Date(data.dateOfBirth), new Date(data.employmentDetails.professionBeginDate)) < 12 * 14)
                return false;
            return true;
        },
        { message: 'professionBeginDate must be at least 14 years after dateOfBirth' },
    );
export type SchemaConsorsSubscriber = z.infer<typeof schemaConsorsSubscriber>;
